import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { NorthlineMobileMenu } from "./NorthlineMobileMenu";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";

function NorthlineStyles() {
    return (
        <style>{`
            [data-web-template="northline-stay"] .site-builder-section {
                isolation: isolate;
                padding-inline: 1.25rem;
                position: relative;
            }

            [data-web-template="northline-stay"] .site-builder-section:not([data-web-region="header"])::after {
                background: var(--site-border);
                bottom: 0;
                content: "";
                left: max(1.25rem, calc((100% - var(--site-container)) / 2));
                pointer-events: none;
                position: absolute;
                top: 0;
                width: 1px;
                z-index: 0;
            }

            [data-web-template="northline-stay"] .site-builder-grid {
                display: grid;
                gap: clamp(1.25rem, 3vw, 3rem);
                margin-inline: auto;
                position: relative;
                width: min(100%, var(--site-container));
                z-index: 1;
            }

            [data-web-template="northline-stay"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="northline-stay"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="northline-stay"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1.2rem;
                min-width: 0;
            }

            [data-web-template="northline-stay"] .site-builder-grid-item,
            [data-web-template="northline-stay"] [data-site-builder-block] {
                min-width: 0;
            }

            [data-web-template="northline-stay"] .site-builder-text,
            [data-web-template="northline-stay"] .site-builder-form-title,
            [data-web-template="northline-stay"] .site-builder-form-text {
                max-width: 100%;
                overflow-wrap: break-word;
            }

            [data-web-template="northline-stay"] .site-builder-column-inline {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="northline-stay"] .site-builder-navigation {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: clamp(1rem, 2vw, 1.9rem);
                justify-content: center;
            }

            [data-web-template="northline-stay"] .site-builder-navigation-item {
                display: inline-flex;
            }

            [data-web-template="northline-stay"] .site-builder-navigation-link,
            [data-web-template="northline-stay"] [data-document-role="headerNavigation"] .site-builder-text {
                display: inline-flex;
                position: relative;
                width: auto !important;
            }

            [data-web-template="northline-stay"] [data-document-role="headerNavigation"] .site-builder-text::after {
                background: var(--site-accent);
                bottom: -.45rem;
                content: "";
                height: 2px;
                left: 0;
                position: absolute;
                transform: scaleX(0);
                transform-origin: left;
                transition: transform 180ms ease;
                width: 100%;
            }

            [data-web-template="northline-stay"] [data-document-role="headerNavigation"] .site-builder-text:hover::after,
            [data-web-template="northline-stay"] [data-document-role="headerNavigation"] .site-builder-text:focus-visible::after {
                transform: scaleX(1);
            }

            [data-web-template="northline-stay"] :where(a, button, input, select, textarea):focus-visible {
                outline: 3px solid var(--site-accent);
                outline-offset: 3px;
            }

            [data-web-template="northline-stay"] [data-web-region="header"] {
                position: sticky;
                top: 0;
                z-index: 40;
            }

            [data-web-template="northline-stay"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="northline-stay"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template="northline-stay"] [data-document-role="headerCta"],
            [data-web-template="northline-stay"] [data-document-role="localeActions"] {
                flex: 0 0 auto;
            }

            [data-web-template="northline-stay"] [data-document-role="routeMarker"] {
                box-shadow: 0 0 0 5px color-mix(in srgb, var(--site-background) 72%, transparent);
            }

            [data-web-template="northline-stay"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                column-gap: 12px;
                display: grid;
                grid-template-columns: repeat(12, minmax(0, 1fr));
                row-gap: 24px;
                width: 100%;
            }

            [data-web-template="northline-stay"] [data-document-role="heroEyebrow"],
            [data-web-template="northline-stay"] [data-document-role="heroHeading"],
            [data-web-template="northline-stay"] [data-document-role="heroDescription"],
            [data-web-template="northline-stay"] [data-document-role="heroProof"] {
                grid-column: 1 / -1;
            }

            /* Keep already-applied snapshots with the former viewport-sized H1 safe. */
            [data-web-template="northline-stay"] [data-document-role="heroHeading"][style*="clamp(54px, 8vw, 112px)"],
            [data-web-template="northline-stay"] [data-document-role="heroHeading"][style*="clamp(54px,8vw,112px)"] {
                font-size: clamp(48px, 6cqw, 76px) !important;
            }

            [data-web-template="northline-stay"] [data-document-role="heroPrimaryAction"] {
                grid-column: 1 / span 6;
            }

            [data-web-template="northline-stay"] [data-document-role="heroSecondaryAction"] {
                grid-column: 7 / span 6;
            }

            [data-web-template="northline-stay"] [data-document-role="heroVisual"],
            [data-web-template="northline-stay"] [data-document-role="editorialVisual"] {
                position: relative;
            }

            [data-web-template="northline-stay"] [data-document-role="heroVisual"]::before {
                border: 1px solid var(--site-accent);
                content: "";
                inset: 18px -18px -18px 18px;
                pointer-events: none;
                position: absolute;
                z-index: -1;
            }

            [data-web-template="northline-stay"] [data-document-role="editorialVisual"] .site-builder-image,
            [data-web-template="northline-stay"] [data-document-role="heroVisual"] .site-builder-image {
                min-height: 100%;
            }

            [data-web-template="northline-stay"] [data-document-role="heroFloatingMarker"] {
                bottom: 24px;
                left: -24px;
                position: absolute;
                z-index: 2;
            }

            [data-web-template="northline-stay"] .site-builder-card {
                position: relative;
            }

            [data-web-template="northline-stay"] .site-builder-card::after {
                background: var(--site-accent);
                content: "";
                height: 2px;
                left: 0;
                position: absolute;
                top: 0;
                transform: scaleX(.18);
                transform-origin: left;
                transition: transform 180ms ease;
                width: 100%;
            }

            [data-web-template="northline-stay"] .site-builder-card:hover::after,
            [data-web-template="northline-stay"] .site-builder-card:focus-within::after {
                transform: scaleX(1);
            }

            [data-web-template="northline-stay"] .site-builder-form-title {
                font-family: var(--site-heading-font);
                font-size: clamp(1.8rem, 3vw, 2.7rem);
                font-weight: 500;
                letter-spacing: -.035em;
                line-height: 1;
            }

            [data-web-template="northline-stay"] [data-web-region="footer"] .site-builder-navigation {
                align-items: flex-start;
                flex-direction: column;
                gap: .7rem;
            }

            [data-web-template="northline-stay"] :where(
                [data-document-role="brand"],
                [data-document-role="footerBrand"],
                [data-document-role="footerNavigation"],
                [data-document-role="footerContact"]
            ) a.site-builder-text {
                color: var(--site-on-primary) !important;
            }

            [data-web-template="northline-stay"] .northline-mobile-menu-toggle {
                display: none;
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="northline-stay"] *,
                [data-web-template="northline-stay"] *::before,
                [data-web-template="northline-stay"] *::after {
                    scroll-behavior: auto !important;
                    transition-duration: .01ms !important;
                }
            }

            @container (min-width: 901px) and (max-width: 1280px) {
                [data-web-template="northline-stay"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]) {
                    grid-column: 1 / span 6 !important;
                }

                [data-web-template="northline-stay"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                    grid-column: 7 / span 6 !important;
                }
            }

            @container (max-width: 900px) {
                [data-web-template="northline-stay"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="northline-stay"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="northline-stay"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]),
                [data-web-template="northline-stay"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="northline-stay"] .site-builder-column[style*="grid-template-columns"] {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="northline-stay"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                    grid-template-columns: minmax(0, 1fr);
                }

                [data-web-template="northline-stay"] [data-document-role="heroEyebrow"],
                [data-web-template="northline-stay"] [data-document-role="heroHeading"],
                [data-web-template="northline-stay"] [data-document-role="heroDescription"],
                [data-web-template="northline-stay"] [data-document-role="heroPrimaryAction"],
                [data-web-template="northline-stay"] [data-document-role="heroSecondaryAction"],
                [data-web-template="northline-stay"] [data-document-role="heroProof"] {
                    grid-column: 1;
                }

                [data-web-template="northline-stay"] [data-document-role="heroVisual"]::before {
                    inset: 12px -10px -10px 12px;
                }

                [data-web-template="northline-stay"] [data-document-role="heroFloatingMarker"] {
                    left: 16px;
                }

                [data-web-template="northline-stay"] [data-web-region="header"] {
                    min-height: 72px;
                    padding-inline: 1rem;
                }

                [data-web-template="northline-stay"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="northline-stay"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="northline-stay"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="northline-stay"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="northline-stay"] [data-web-region="header"] .northline-mobile-menu-toggle {
                    align-items: center;
                    background: transparent;
                    border: 1px solid color-mix(in srgb, var(--site-background) 26%, transparent);
                    border-radius: var(--site-radius);
                    cursor: pointer;
                    display: grid;
                    gap: 7px;
                    height: 44px;
                    justify-self: end;
                    padding: 10px;
                    place-content: center;
                    position: absolute;
                    right: 1rem;
                    top: 14px;
                    width: 44px;
                    z-index: 3;
                }

                [data-web-template="northline-stay"] .northline-mobile-menu-toggle > span {
                    background: var(--site-accent);
                    display: block;
                    height: 2px;
                    transition: transform 160ms ease;
                    width: 24px;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] {
                    bottom: 0;
                    left: 0;
                    overflow-y: auto;
                    position: fixed;
                    right: 0;
                    top: 0;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] > .site-builder-grid {
                    align-content: start;
                    min-height: 100%;
                    padding-bottom: 2rem;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-row: 2 !important;
                    margin-top: clamp(3rem, 10vh, 6rem);
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-row: 3 !important;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] [data-document-role="headerNavigation"],
                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0 !important;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid color-mix(in srgb, var(--site-background) 18%, transparent);
                    padding-block: 1rem;
                    width: 100%;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text {
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(2.2rem, 9vw, 3.5rem) !important;
                    font-weight: 500 !important;
                    line-height: 1 !important;
                    width: 100% !important;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] [data-document-role="headerActions"] {
                    gap: .75rem !important;
                    margin-top: 1.25rem;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] [data-document-role="headerActions"] > *,
                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] [data-document-role="headerCta"] {
                    width: 100% !important;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] .northline-mobile-menu-toggle > span:first-child {
                    transform: translateY(4.5px) rotate(45deg);
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] .northline-mobile-menu-toggle > span:last-child {
                    transform: translateY(-4.5px) rotate(-45deg);
                }
            }

            @container (max-width: 640px) {
                [data-web-template="northline-stay"] [data-document-role="heroVisual"],
                [data-web-template="northline-stay"] [data-document-role="heroVisual"] .site-builder-image {
                    min-height: clamp(440px, 120vw, 500px) !important;
                }

                [data-web-template="northline-stay"] .site-builder-section {
                    padding-inline: 1rem;
                }

                [data-web-template="northline-stay"] .site-builder-section:not([data-web-region="header"])::after {
                    left: 1rem;
                }

                [data-web-template="northline-stay"] [data-document-role="heroPrimaryAction"],
                [data-web-template="northline-stay"] [data-document-role="heroSecondaryAction"] {
                    width: 100% !important;
                }

                [data-web-template="northline-stay"] .site-builder-column,
                [data-web-template="northline-stay"] .site-builder-form-fields {
                    grid-template-columns: minmax(0, 1fr) !important;
                }
            }

            /* Alcatraz-inspired cinematic opening: image first, native scroll, compact glass header. */
            [data-web-template="northline-stay"] [data-web-region="header"] {
                background: linear-gradient(180deg, rgba(3, 10, 9, .3), transparent 72%), var(--site-primary) !important;
                border-color: transparent !important;
                left: 0;
                min-height: 104px;
                position: fixed !important;
                right: 0;
                top: 0;
                transition: background 420ms ease, backdrop-filter 420ms ease, min-height 420ms ease, box-shadow 420ms ease;
                width: 100%;
                z-index: 70;
            }

            [data-web-template="northline-stay"] [data-web-region="header"][data-northline-header-scrolled] {
                backdrop-filter: blur(18px) saturate(1.25);
                background: var(--site-primary) !important;
                border-color: color-mix(in srgb, var(--site-background) 16%, transparent) !important;
                box-shadow: 0 18px 46px rgba(3, 10, 9, .22);
                min-height: 74px;
            }

            [data-web-template="northline-stay"] [data-web-region="homeHero"] {
                min-height: 100svh !important;
                overflow: hidden;
                padding: 0 !important;
                position: relative;
            }

            [data-web-template="northline-stay"] [data-web-region="homeHero"] > .site-builder-grid {
                align-items: end;
                isolation: isolate;
                min-height: 100svh;
                width: 100%;
                max-width: none;
            }

            [data-web-template="northline-stay"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                grid-column: 1 / -1 !important;
                grid-row: 1 !important;
                inset: 0;
                min-height: 100svh;
                position: absolute;
                z-index: 0;
            }

            [data-web-template="northline-stay"] [data-document-role="heroVisual"] {
                height: 100svh;
                min-height: 100svh !important;
                position: relative;
            }

            [data-web-template="northline-stay"] [data-document-role="heroFloatingMarker"] {
                bottom: 18px;
                left: 20px;
            }

            [data-web-template="northline-stay"] [data-document-role="heroVisual"]::before {
                background:
                    linear-gradient(90deg, rgba(3, 10, 9, .88) 0%, rgba(3, 10, 9, .48) 48%, rgba(3, 10, 9, .12) 76%),
                    linear-gradient(0deg, rgba(3, 10, 9, .82), transparent 52%);
                border: 0;
                inset: 0;
                position: absolute;
                z-index: 1;
            }

            [data-web-template="northline-stay"] [data-document-role="heroVisual"] .site-builder-image,
            [data-web-template="northline-stay"] [data-document-role="heroVisual"] img {
                height: 100% !important;
                min-height: 100svh !important;
                object-fit: cover;
                transform: scale(1.018);
                transition: transform 7.6s cubic-bezier(.2,.7,.2,1);
                width: 100%;
            }

            [data-web-template="northline-stay"] [data-web-region="homeHero"]:hover [data-document-role="heroVisual"] img {
                transform: scale(1);
            }

            [data-web-template="northline-stay"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]) {
                grid-column: 1 / span 8 !important;
                grid-row: 1 !important;
                margin-left: max(1.25rem, calc((100vw - var(--site-container)) / 2));
                max-width: min(760px, calc(100vw - 2.5rem));
                padding: clamp(9rem, 18vh, 13rem) 0 clamp(4rem, 10vh, 7rem);
                position: relative;
                z-index: 3;
            }

            [data-web-template="northline-stay"] [data-web-region="homeHero"] [data-document-role="heroHeading"] {
                font-size: clamp(4rem, 7.2vw, 7.4rem) !important;
                letter-spacing: -.045em !important;
                line-height: .84 !important;
                text-shadow: 0 12px 50px rgba(0, 0, 0, .32);
            }

            [data-web-template="northline-stay"] [data-web-region="homeHero"] :where([data-document-role="heroDescription"], [data-document-role="heroProof"]) {
                color: rgba(255, 250, 241, .84) !important;
            }

            [data-web-template="northline-stay"] [data-web-region="homeHero"] [data-document-role="heroSecondaryAction"] {
                background: rgba(3, 10, 9, .28) !important;
                border-color: rgba(255, 250, 241, .7) !important;
                color: #fffaf1 !important;
            }

            [data-web-template="northline-stay"] [data-web-region="stayEssentials"] {
                background: var(--site-primary) !important;
                color: var(--site-background);
            }

            [data-web-template="northline-stay"] :where(h1, h2, h3, [data-text-role="heading"]) {
                text-wrap: balance;
            }

            [data-web-template="northline-stay"] .site-builder-card {
                overflow: hidden;
                transition: border-color 220ms ease, box-shadow 220ms ease, transform 220ms ease;
            }

            [data-web-template="northline-stay"] .site-builder-card:hover,
            [data-web-template="northline-stay"] .site-builder-card:focus-within {
                border-color: color-mix(in srgb, var(--site-accent) 68%, transparent) !important;
                box-shadow: 0 24px 58px rgba(23, 56, 49, .13);
                transform: translateY(-4px);
            }

            [data-web-template="northline-stay"] [data-document-role="editorialVisual"] {
                overflow: hidden;
            }

            [data-web-template="northline-stay"] [data-document-role="editorialVisual"] img {
                transition: filter 520ms ease, transform 760ms cubic-bezier(.2,.7,.2,1);
            }

            [data-web-template="northline-stay"] [data-document-role="editorialVisual"]:hover img {
                filter: saturate(1.08) contrast(1.03);
                transform: scale(1.045);
            }

            [data-web-template="northline-stay"] [data-northline-reveal="true"] {
                opacity: 0;
                transform: translateY(22px);
                transition: opacity 560ms ease, transform 560ms cubic-bezier(.2,.7,.2,1);
            }

            [data-web-template="northline-stay"] [data-northline-revealed="true"],
            [data-web-template="northline-stay"][data-web-template-editor="true"] [data-northline-reveal="true"] {
                opacity: 1;
                transform: none;
            }

            @container (max-width: 900px) {
                [data-web-template="northline-stay"] [data-web-region="header"] {
                    min-height: 72px;
                }

                [data-web-template="northline-stay"] [data-web-region="header"] .northline-mobile-menu-toggle {
                    opacity: 0;
                    pointer-events: none;
                    transform: translateY(-8px);
                    transition: opacity 260ms ease, transform 260ms ease;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-header-scrolled] .northline-mobile-menu-toggle,
                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] .northline-mobile-menu-toggle {
                    opacity: 1;
                    pointer-events: auto;
                    transform: none;
                }

                [data-web-template="northline-stay"] [data-web-region="header"][data-northline-mobile-menu-open] {
                    background:
                        radial-gradient(circle at 88% 12%, color-mix(in srgb, var(--site-accent) 28%, transparent), transparent 34%),
                        var(--site-primary) !important;
                }

                [data-web-template="northline-stay"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                    position: absolute;
                }

                [data-web-template="northline-stay"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]) {
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                    margin: 0;
                    max-width: none;
                    padding: 8.5rem 1rem 3.5rem;
                    position: relative;
                }

                [data-web-template="northline-stay"] [data-web-region="homeHero"] [data-document-role="heroHeading"] {
                    font-size: clamp(3.35rem, 16vw, 5.4rem) !important;
                }

                [data-web-template="northline-stay"] [data-document-role="heroVisual"]::before {
                    background:
                        linear-gradient(0deg, rgba(3, 10, 9, .92) 0%, rgba(3, 10, 9, .3) 74%),
                        linear-gradient(90deg, rgba(3, 10, 9, .55), transparent);
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="northline-stay"] [data-northline-reveal="true"] {
                    opacity: 1;
                    transform: none;
                }

                [data-web-template="northline-stay"] [data-document-role="heroVisual"] img {
                    transform: none;
                }
            }
        `}</style>
    );
}

function NorthlineFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main
            className="px-5 py-20 sm:px-6"
            data-builder-document-region="page"
        >
            <article className="mx-auto max-w-[900px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-primary)] [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-7 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-6xl [&_h1]:font-medium [&_h1]:leading-[.92] [&_h1]:text-[var(--site-text)] [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-4xl [&_h2]:font-medium [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const northlineStayTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "northline-stay",
        name: "Hotel, penzion a apartmány",
        type: "website",
        category: "hospitality",
        description: "Moderní editoriální šablona pro apartmány, hotely a penziony, která propojuje ubytování, zážitky a praktické plánování pobytu.",
        previewImage: "/templates/northline-stay/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["northline-stay"],
        pageTypes: {
            home: {
                label: "Hospitality homepage",
                slots: ["richContent"],
            },
            content: {
                label: "Hospitality content page",
                slots: ["richContent"],
            },
            contact: {
                label: "Hospitality contact page",
                slots: ["richContent"],
            },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            northlineStayTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                data-web-template="northline-stay"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                className="min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <NorthlineStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <NorthlineMobileMenu
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <NorthlineFallbackPage
                            props={props}
                            design={design}
                            LinkComponent={LinkComponent}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
